import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { Tonality } from '@dsny/dsny-component-library'
import { Search, Arrowback } from '@dsny/dsny-component-library/icons'
import { FilterGrid } from '../MobileFiltersPanel/MobileFiltersPanel.styles'
import FilterPill from '../FilterPill/FilterPill'

const SearchableFilterSection = ({
  title,
  options = [],
  localSelectedFilters = [],
  handleFilterToggle,
  filterType
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [showSearchInput, setShowSearchInput] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const handleSearchClick = () => {
    setShowSearchInput(!showSearchInput)
    if (showSearchInput) {
      setSearchQuery('')
    }
  }

  const filteredOptions = options
    .filter((option) =>
      option.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const aIsSelected = localSelectedFilters.some(
        (f) => f.type === filterType && f.id === a.id
      )
      const bIsSelected = localSelectedFilters.some(
        (f) => f.type === filterType && f.id === b.id
      )

      // First sort by selection status (selected items first)
      if (bIsSelected !== aIsSelected) {
        return bIsSelected - aIsSelected
      }

      // Then sort alphabetically
      return a.name.localeCompare(b.name)
    })

  return (
    <>
      <AnimatePresence mode="wait">
        {!showSearchInput ? (
          <motion.div
            key={`${filterType}-title`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <Tonality.UIText
              textStyle="bodyBold"
              style={{
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center'
              }}
              color={theme.colors.neutralW90}
            >
              {title}

              <Search
                width={20}
                height={20}
                color={theme.colors.neutralW90}
                style={{
                  fontSize: 16,
                  marginLeft: 8,
                  cursor: 'pointer'
                }}
                onClick={handleSearchClick}
              />
            </Tonality.UIText>
          </motion.div>
        ) : (
          <motion.div
            key={`${filterType}-search`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16
            }}
          >
            <Arrowback
              width={20}
              height={20}
              color={theme.colors.neutralW90}
              style={{ cursor: 'pointer' }}
              onClick={handleSearchClick}
            />
            <div style={{ flex: 1, position: 'relative' }}>
              <Tonality.TextField
                id={`${filterType}-search`}
                name={`${filterType}-search`}
                setting="player"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                labelText=""
                placeholder={t('home.search')}
                inputStyle={{
                  maxHeight: 44,
                  paddingRight: 40
                }}
                wrapperStyles={{ marginBottom: 0 }}
                tailingIcon="Search"
                tailingStyle={{
                  marginRight: 20,
                  transform: 'translateY(-50%)',
                  fill: theme.colors.neutralW90
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FilterGrid>
        {filteredOptions.map((option) => {
          const isSelected = localSelectedFilters.some(
            (f) => f.type === filterType && f.id === option.id
          )
          return (
            <FilterPill
              key={option.id}
              option={option}
              filterType={filterType}
              isSelected={isSelected}
              onToggle={handleFilterToggle(filterType, options)}
            />
          )
        })}
      </FilterGrid>
    </>
  )
}

export default SearchableFilterSection
